import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useHistory } from "react-router-dom";
import { buildPromoStoreDetailsPath } from "./promoStoreReportUtils";
import InstancePromoStoreProductReport from "./InstancePromoStoreProductReport";
import "./promostore-reports.css";

const reportSections = [
  {
    title: "Active reports",
    reports: [
      {
        key: "products",
        title: "Product Performance Report",
        description: "View detailed product performance, sales trends, revenue, and top-performing items.",
        Icon: AssessmentOutlinedIcon,
        active: true,
      },
      {
        key: "orders",
        title: "Orders Report",
        description: "Preview and analyze store orders, totals, statuses, payments, shipping, and customer details.",
        Icon: ReceiptLongOutlinedIcon,
        active: true,
      },
    ],
  },
  {
    title: "Sales",
    reports: [
      { key: "sales-summary", title: "Sales Summary Report", description: "Store-level sales overview and period summaries.", Icon: TrendingUpOutlinedIcon },
      { key: "customer-purchases", title: "Customer Purchases Report", description: "Customer purchase behavior and repeat ordering insights.", Icon: PeopleAltOutlinedIcon },
    ],
  },
  {
    title: "Finance",
    reports: [
      { key: "tax", title: "Tax Report", description: "Tax totals and jurisdiction breakdowns.", Icon: AccountBalanceOutlinedIcon },
      { key: "payment", title: "Payment Report", description: "Payment method and collection summaries.", Icon: PaymentsOutlinedIcon },
      { key: "quote-po", title: "Quote / P.O. Report", description: "Quote and purchase-order request activity.", Icon: RequestQuoteOutlinedIcon },
    ],
  },
  {
    title: "Operations",
    reports: [
      { key: "inventory", title: "Inventory Report", description: "Inventory availability and movement once authoritative data is enabled.", Icon: Inventory2OutlinedIcon },
      { key: "shipping", title: "Shipping Report", description: "Shipping methods, labels, and fulfillment activity.", Icon: LocalShippingOutlinedIcon },
      { key: "sync-health", title: "Sync Health Report", description: "PromoStore synchronization status and health metrics.", Icon: SyncOutlinedIcon },
    ],
  },
  {
    title: "Other Reports",
    reports: [
      { key: "refunds-cancellations", title: "Refunds & Cancellations Report", description: "Refund and cancellation activity when supported by synchronized data.", Icon: ReplayOutlinedIcon },
      { key: "users", title: "User Report", description: "Store user activity and account summaries.", Icon: PeopleAltOutlinedIcon },
      { key: "store-comparison", title: "Store Comparison Report", description: "Compare performance across PromoStores.", Icon: StorefrontOutlinedIcon },
    ],
  },
];

const InstancePromoStoreReports = ({ instanceUniqueId, promoStoreId, store = null }) => {
  const storeId = promoStoreId || store?.id;
  const history = useHistory();
  const [productPreviewOpen, setProductPreviewOpen] = useState(false);
  const availableCount = reportSections.reduce((count, section) => count + section.reports.filter((report) => report.active).length, 0);
  const plannedCount = reportSections.reduce((count, section) => count + section.reports.filter((report) => !report.active).length, 0);

  const openReport = (reportKey) => {
    if (reportKey === "products") {
      setProductPreviewOpen(true);
      return;
    }

    if (reportKey === "orders") {
      history.push(`${buildPromoStoreDetailsPath(instanceUniqueId, storeId, "orders")}?previewOrdersReport=1`);
    }
  };

  return (
    <Box className="promostore-reports-root">
      <Box className="promostore-reports-shell">
        <Box className="promostore-reports-header">
          <Box className="promostore-reports-heading">
            <Box className="promostore-reports-heading-icon" aria-hidden="true"><InsertChartOutlinedRoundedIcon /></Box>
            <Box>
              <Typography className="promostore-reports-eyebrow">PROMOSTORE ANALYTICS</Typography>
              <Typography variant="h5" component="h2" fontWeight={800}>Reports & Insights</Typography>
              <Typography variant="body2" color="text.secondary">
                Explore your store performance, orders, and upcoming reporting tools.
              </Typography>
            </Box>
          </Box>
          <Box className="promostore-reports-overview" aria-label="Report availability">
            <Box className="promostore-reports-overview-item">
              <strong>{availableCount}</strong><span>Available now</span>
            </Box>
            <Box className="promostore-reports-overview-divider" aria-hidden="true" />
            <Box className="promostore-reports-overview-item">
              <strong>{plannedCount}</strong><span>Planned reports</span>
            </Box>
          </Box>
        </Box>

        {reportSections.map((section) => (
          <Box key={section.title} component="section" className={`promostore-reports-section${section.title === "Active reports" ? " is-active" : ""}`} aria-label={section.title}>
            <Box className="promostore-reports-section-heading">
              <Box>
                <Typography className="promostore-reports-section-title">{section.title}</Typography>
                {section.title === "Active reports" && <Typography className="promostore-reports-section-subtitle">Ready to explore</Typography>}
              </Box>
              <Typography className="promostore-reports-section-count">{section.reports.length} {section.reports.length === 1 ? "report" : "reports"}</Typography>
            </Box>
            <Box className="promostore-reports-grid">
              {section.reports.map(({ key, title, description, Icon, active }) => {
                const isDisabled = !active;
                const statusLabel = active && !isDisabled ? "OPEN" : "COMING SOON";
                const CardComponent = active && !isDisabled ? "button" : "div";

                return (
                  <CardComponent
                    key={key}
                    type={CardComponent === "button" ? "button" : undefined}
                    className={`promostore-report-card${isDisabled ? " is-disabled" : " is-available"}`}
                    onClick={active && !isDisabled ? () => openReport(key) : undefined}
                    aria-disabled={isDisabled || undefined}
                    tabIndex={active && !isDisabled ? 0 : -1}
                  >
                    <Box className="promostore-report-card-topline">
                      <Box className="promostore-report-card-icon"><Icon fontSize="small" /></Box>
                      <span className={`promostore-report-card-status ${statusLabel === "OPEN" ? "is-open" : "is-coming-soon"}`}>{statusLabel}</span>
                    </Box>
                    <Box className="promostore-report-card-content">
                      <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    </Box>
                    {active && <Box className="promostore-report-card-action" aria-hidden="true">View report <ArrowForwardRoundedIcon fontSize="small" /></Box>}
                  </CardComponent>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
      <InstancePromoStoreProductReport
        previewOpen={productPreviewOpen}
        onPreviewClose={() => setProductPreviewOpen(false)}
        instanceUniqueId={instanceUniqueId}
        promoStoreId={storeId}
        store={store}
      />
    </Box>
  );
};

InstancePromoStoreReports.propTypes = {
  instanceUniqueId: PropTypes.string.isRequired,
  promoStoreId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  store: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
};

InstancePromoStoreReports.defaultProps = {
  promoStoreId: null,
  store: null,
};

export default InstancePromoStoreReports;
